import React from 'react';
import { useSelector } from 'react-redux';
import { Link, withRouter } from 'react-router-dom';
import { sortBy } from 'lodash';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { AMAZON_BASE_URL, FONTS_NS } from './Constants';
import withMenu from './MenuHOC';
import { AppStore, CategoryType, Store } from './types';
import { getFontTagCodes } from './Helpers';
import Filter from './Filter';

enum NewUpdated {
    NEW = 1,
    UPDATED = 2
}

const Fonts: React.FC<{}> = () => {
    const { t } = useTranslation();
    const filters = useSelector(({ app }: Store) =>
        (app.filters || []).filter(({ type }) => type !== CategoryType.WEIGHTS)
    );
    const fonts = useSelector(({ app }: Store) =>
        sortBy(
            app.fonts?.filter(({ isPackage, isMainFont }) => isMainFont && !isPackage),
            ({ name }) => name
        )
    );

    const [newUpdatedOptions, setNewUpdatedOptions] = React.useState<
        AppStore['filters'][number]['options']
    >([
        { checked: false, nameEn: 'New', nameGr: 'Νέα', id: NewUpdated.NEW },
        {
            checked: false,
            nameEn: 'Updated',
            nameGr: 'Διορθωμένη',
            id: NewUpdated.UPDATED
        }
    ]);

    const getPromoImage = React.useCallback(
        (code: string, hasPromoImgConfigured: boolean): string =>
            hasPromoImgConfigured
                ? `${AMAZON_BASE_URL}/fonts/${code}/promo.png`
                : `${AMAZON_BASE_URL}/general/not_yet.png`,
        []
    );

    const bothChecked = newUpdatedOptions.every(({ checked }) => checked);
    const newChecked = newUpdatedOptions.find(
        ({ id }) => id === NewUpdated.NEW
    ).checked;
    const updatedChecked = newUpdatedOptions.find(
        ({ id }) => id === NewUpdated.UPDATED
    ).checked;

    const filteredFonts = filters.reduce((acc, { options }) => {
        const selectedOptions = options.filter(({ checked }) => checked);

        const filtered = acc.filter(({ fontTagIDs }) =>
            selectedOptions.some(({ id }) => fontTagIDs.includes(id))
        );

        // all unchecked => show all fonts
        if (newUpdatedOptions.every(({ checked }) => !checked)) {
            return filtered;
        }

        return filtered.filter(({ isNewFont, hasNewVersion }) => {
            if (bothChecked) {
                return isNewFont && hasNewVersion;
            }

            if (newChecked) {
                return isNewFont;
            }

            if (updatedChecked) {
                return hasNewVersion;
            }

            return true;
        });
    }, fonts);

    const handleNewUpdatedFilterChange = React.useCallback((id: number) => {
        setNewUpdatedOptions(values =>
            values.map(option =>
                option.id === id ? { ...option, checked: !option.checked } : option
            )
        );
    }, []);

    const allTags = filters.reduce(
        (acc, { options }) => acc.concat(options),
        [] as AppStore['filters'][number]['options']
    );

    return (
        <div className='fonts-wrapper'>
            <div className='filters'>
                {filters.map(f => (
                    <Filter key={f.id} category={f} />
                ))}
                <Filter
                    category={{
                        id: CategoryType.NEW_UPDATED,
                        nameEn: 'New/Updated Font',
                        nameGr: 'Νέα/Διορθωμένη Γραμματοσειρά',
                        type: CategoryType.NEW_UPDATED,
                        options: newUpdatedOptions
                    }}
                    width={270}
                    onChange={handleNewUpdatedFilterChange}
                />
            </div>
            <div className='fonts'>
                {filteredFonts.map(
                    ({
                        id,
                        name,
                        fullPageName,
                        isNewFont,
                        code,
                        hasPromoImgConfigured,
                        hasNewVersion,
                        supportedLanguageIDs,
                        weightIDs,
                        isFree
                    }) => {
                        const tags = getFontTagCodes(supportedLanguageIDs, allTags);

                        return (
                            <Link
                                to={`/fonts/${fullPageName}`}
                                key={id}
                                className='font-wrapper'>
                                <div className='top'>
                                    <div className='font-name'>
                                        <div
                                            className={clsx('name', {
                                                small:
                                                    name.length > 10 &&
                                                    tags.length > 3 &&
                                                    (isFree ||
                                                        isNewFont ||
                                                        hasNewVersion)
                                            })}>
                                            {name}
                                        </div>
                                        {isFree && (
                                            <div className='free-font'>FREE</div>
                                        )}
                                        {isNewFont && (
                                            <div className='new-font'>
                                                {t(`${FONTS_NS}.new_font`)}
                                            </div>
                                        )}
                                        {hasNewVersion && (
                                            <div className='updated-font'>UPD</div>
                                        )}
                                    </div>
                                    <div className='font-details'>
                                        <div className='weight'>
                                            {weightIDs.length} {t(`${FONTS_NS}.weight`)}
                                        </div>
                                        <div className='language'>{tags.join('/')}</div>
                                    </div>
                                </div>
                                <div className='strip' />
                                <div>
                                    <img
                                        src={getPromoImage(code, hasPromoImgConfigured)}
                                    />
                                </div>
                            </Link>
                        );
                    }
                )}
            </div>
        </div>
    );
};

export default withRouter(withMenu(Fonts));
